<template>
  <b-modal
    id="royalty-report-info-modal"
    :visible="value"
    size="xl"
    scrollable
    hide-footer
    modal-class="royalty-report-info-modal"
    body-class="p-0"
    @change="onVisibleChange"
  >
    <template #modal-header="{ close }">
      <div class="report-info-header w-100 d-flex align-items-start justify-content-between">
        <div>
          <h4 class="mb-25 font-weight-bolder text-white">Report Info</h4>
          <p class="mb-0 text-white-50 small">
            Explanation + formula beside the values from this statement
          </p>
        </div>
        <b-button variant="link" class="text-white p-0 close-btn" @click="close()">
          &times;
        </b-button>
      </div>
    </template>

    <div class="report-info-body">
      <div class="legend-strip">
        <div class="legend-title">Symbols used in formulas</div>
        <div class="legend-chips">
          <span class="chip"><strong>R</strong> Total theatrical rental</span>
          <span class="chip"><strong>E</strong> P&amp;A expense total</span>
          <span class="chip"><strong>M</strong> Minimum Guarantee (MG)</span>
          <span class="chip"><strong>%</strong> Contract share (e.g. 70% → 0.70)</span>
        </div>
        <div v-if="hasReport" class="live-banner mt-75">
          Showing actual values from the current report
        </div>
      </div>

      <div class="section-nav">
        <button
          v-for="sec in sections"
          :key="sec.id"
          type="button"
          class="nav-pill"
          :class="{ active: activeSection === sec.id }"
          @click="scrollToSection(sec.id)"
        >
          {{ sec.short }}
        </button>
      </div>

      <div class="sections-scroll">
        <div
          v-for="sec in sections"
          :id="'info-sec-' + sec.id"
          :key="sec.id"
          class="info-section"
        >
          <div class="section-head">
            <span class="section-index">{{ sec.letter }}</span>
            <div>
              <h5 class="mb-0">{{ sec.title }}</h5>
              <p v-if="sec.intro" class="mb-0 text-muted small mt-25">{{ sec.intro }}</p>
            </div>
          </div>

          <div v-if="sec.columns && sec.columns.length" class="mb-1">
            <div class="sub-label">Columns</div>
            <div class="item-grid">
              <div v-for="(col, i) in sec.columns" :key="'c' + i" class="info-card compact">
                <div class="item-title">{{ col.name }}</div>
                <div class="item-desc">{{ col.explanation }}</div>
                <div v-if="live(col.valueKey)" class="value-pill">
                  {{ live(col.valueKey) }}
                </div>
              </div>
            </div>
          </div>

          <div class="item-list">
            <template v-for="(row, i) in sec.rows">
              <div
                v-if="row.showConditionBanner"
                :key="'cond-' + sec.id + '-' + i"
                class="condition-banner"
              >
                <div class="condition-title">When is this section shown?</div>
                <div class="condition-text">
                  {{ row.conditionText }}
                </div>
                <div class="condition-live">
                  <span>This report:</span>
                  <strong :class="corridorSectionVisible ? 'on' : 'off'">
                    {{ corridorSectionVisible ? "Shown (condition met)" : "Hidden (condition not met)" }}
                  </strong>
                  <span class="condition-switches">
                    Lic Corridor P&amp;A =
                    <b>{{ live("licCorridorPa") || "—" }}</b>
                    · Lic Corridor MG =
                    <b>{{ live("licCorridorMg") || "—" }}</b>
                  </span>
                </div>
              </div>
              <div
                v-if="!row.corridorOnly || corridorSectionVisible || alwaysShowCorridorInfo"
                :key="'r-' + sec.id + '-' + i"
                class="info-card row-card"
                :class="{ dimmed: row.corridorOnly && !corridorSectionVisible }"
              >
                <div class="row-main">
                  <div class="row-left">
                    <div class="item-title">{{ row.name }}</div>
                    <div class="item-desc">{{ row.explanation }}</div>
                    <div v-if="row.formula" class="formula-box">
                      <span class="formula-label">Formula</span>
                      <code>{{ row.formula }}</code>
                    </div>
                    <div v-if="row.rule" class="rule-box">
                      <span class="formula-label">Rule</span>
                      <span>{{ row.rule }}</span>
                    </div>
                  </div>
                  <div class="row-right">
                    <div class="value-label">This report</div>
                    <div
                      class="value-display"
                      :class="{ empty: !live(row.valueKey), yn: row.valueType === 'yn' }"
                    >
                      {{ live(row.valueKey) || "—" }}
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <b-alert v-if="sec.note" show variant="warning" class="mt-1 mb-0 py-75 small">
            {{ sec.note }}
          </b-alert>
        </div>

        <div v-if="isTerritory" id="info-sec-territory" class="info-section">
          <div class="section-head">
            <span class="section-index">T</span>
            <div>
              <h5 class="mb-0">Territory columns</h5>
              <p class="mb-0 text-muted small mt-25">
                Extra layout on the territory report
              </p>
            </div>
          </div>
          <div class="item-list">
            <div class="info-card row-card">
              <div class="row-main">
                <div class="row-left">
                  <div class="item-title">Each territory column</div>
                  <div class="item-desc">
                    That line’s amount for one selected territory only (no
                    double-counting across overlapping groups).
                  </div>
                </div>
                <div class="row-right">
                  <div class="value-label">Territories</div>
                  <div class="value-display">{{ live("territories") || "—" }}</div>
                </div>
              </div>
            </div>
            <div class="info-card row-card">
              <div class="row-main">
                <div class="row-left">
                  <div class="item-title">Total column</div>
                  <div class="item-desc">
                    Deal total for the period — same commercial result as the
                    standard report for the same titles and dates.
                  </div>
                </div>
                <div class="row-right">
                  <div class="value-label">Total Licensor</div>
                  <div class="value-display">{{ live("totalLicensor") || "—" }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div id="info-sec-example" class="info-section example-section">
          <div class="section-head">
            <span class="section-index">Ex</span>
            <div>
              <h5 class="mb-0">This statement — theatrical path</h5>
              <p class="mb-0 text-muted small mt-25">
                Live values from the report you have open
              </p>
            </div>
          </div>
          <div class="example-flow">
            <div class="ex-step">
              <div class="ex-label">Total Rental (R)</div>
              <div class="ex-value">{{ live("totalRental") || "—" }}</div>
            </div>
            <div class="ex-arrow">→</div>
            <div class="ex-step muted">
              <div class="ex-label">P&amp;A shown</div>
              <div class="ex-value">{{ live("pnaTotal") || "—" }}</div>
              <div class="ex-hint">{{ live("deductionPa") === "Yes" ? "in Total Less" : "display only" }}</div>
            </div>
            <div class="ex-arrow">→</div>
            <div class="ex-step">
              <div class="ex-label">Total Less</div>
              <div class="ex-value">{{ live("totalLess") || "—" }}</div>
            </div>
            <div class="ex-arrow">→</div>
            <div class="ex-step">
              <div class="ex-label">Post Recoupment</div>
              <div class="ex-value">{{ live("postRecoup") || "—" }}</div>
              <div class="ex-hint">R − Total Less</div>
            </div>
            <div class="ex-arrow">→</div>
            <div class="ex-step highlight">
              <div class="ex-label">Licensor share {{ live("overagePercent") }}</div>
              <div class="ex-value">{{ live("overageShare") || "—" }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { BModal, BButton, BAlert } from "bootstrap-vue";
import { formatCurrency } from "@/utils/helpers";

const SECTIONS = [
  {
    id: "switches",
    letter: "A",
    short: "Switches",
    title: "Deal switches (Yes / No)",
    intro: "From the contract. They decide whether a line is deducted or only shown.",
    rows: [
      {
        name: "Deduction P&A From Cinematic Gross Receipts",
        explanation: "Should P&A be taken off rental before the overage?",
        rule: "Yes → P&A enters Total Less. No → P&A is shown but not deducted.",
        valueKey: "deductionPa",
        valueType: "yn"
      },
      {
        name: "Deduction MG From Cinematic Gross Receipts",
        explanation: "Should MG be taken off before the overage?",
        rule: "Yes → MG in Total Less. No → MG under Already Paid.",
        valueKey: "deductionMg",
        valueType: "yn"
      },
      {
        name: "Distributor Corridor Prior To Recoupement Of P&A",
        explanation: "Distributor gets a corridor share before overage (on P&A).",
        rule: "Yes → corridor amount is included in Total Less.",
        valueKey: "distCorridorPa",
        valueType: "yn"
      },
      {
        name: "Distributor Corridor Prior To Recoupement Of MG",
        explanation: "Same idea on MG.",
        rule: "Yes → Dist MG corridor in Total Less.",
        valueKey: "distCorridorMg",
        valueType: "yn"
      },
      {
        name: "Distributor Corridor Prior To Recoupement Of P&A and MG",
        explanation:
          "Combined commission on P&A + MG (replaces the two Dist corridors when Yes).",
        rule: "Yes → one combined Dist amount.",
        valueKey: "distCorridorPaMg",
        valueType: "yn"
      },
      {
        name: "Licensor Corridor Prior To Recoupement Of P&A",
        explanation: "Licensor corridor before overage (on P&A).",
        rule: "Yes → Lic P&A corridor; may also open the corridor payable section.",
        valueKey: "licCorridorPa",
        valueType: "yn"
      },
      {
        name: "Licensor Corridor Prior To Recoupement Of MG",
        explanation: "Licensor corridor on MG.",
        rule: "Yes → Lic MG corridor.",
        valueKey: "licCorridorMg",
        valueType: "yn"
      }
    ]
  },
  {
    id: "theatrical",
    letter: "B",
    short: "Theatrical",
    title: "Theatrical block",
    intro: "Cinema performance for the statement period.",
    columns: [
      { name: "Admissions", explanation: "Number of tickets / admissions", valueKey: "admissionsTotal" },
      { name: "Box Office", explanation: "Box-office amount", valueKey: "boxOfficeTotal" },
      { name: "Rental", explanation: "Theatrical rental (main figure for overage)", valueKey: "totalRental" }
    ],
    rows: [
      {
        name: "Accumulated rental",
        explanation: "Activity before the statement “from” date.",
        formula: "Sum where date < From",
        valueKey: "rentalAcc"
      },
      {
        name: "This Period rental",
        explanation: "Activity inside From–To.",
        formula: "Sum where date is between From and To",
        valueKey: "rentalPeriod"
      },
      {
        name: "Total Rental",
        explanation: "Rental used in later calculations.",
        formula: "R = Accumulated rental + This Period rental",
        valueKey: "totalRental"
      }
    ]
  },
  {
    id: "less",
    letter: "C",
    short: "Total Less",
    title: "P&A / MG / corridors / Total Less",
    intro: "What is deducted before calculating the theatrical overage.",
    note:
      "If Deduction P&A = No, P&A can still show on the P&A line while Total Less stays 0 for that part — display does not mean deduction.",
    rows: [
      {
        name: "P&A — Total (displayed)",
        explanation: "P&A costs (always displayed on the report).",
        formula: "E = Accumulated + This Period",
        valueKey: "pnaTotal"
      },
      {
        name: "P&A in Total Less",
        explanation: "Amount actually deducted when Deduction P&A = Yes.",
        formula: "P&A_in_less = E if Deduction P&A = Yes, else 0",
        valueKey: "pnaInLess"
      },
      {
        name: "MG (in Total Less)",
        explanation: "MG included in “less” only if Deduction MG = Yes.",
        formula: "If Deduction MG = Yes → M, else → 0",
        valueKey: "mgInLess"
      },
      {
        name: "Distributor Corridor Of P&A",
        explanation: "Dist corridor on P&A (if switch Yes).",
        formula:
          "Dist & Lic Yes: R > E/(1−d−ℓ) ? [E/(1−d−ℓ)]×d : R×d · Dist only: R > E/(1−d) ? [E/(1−d)]×d : R×d · Else 0",
        valueKey: "distPaAmount"
      },
      {
        name: "Distributor Corridor Of MG",
        explanation: "Dist corridor on MG (if Yes, and not combined commission).",
        formula: "R > M/(1−d_mg) ? [M/(1−d_mg)]×d_mg : R×d_mg · Else 0",
        valueKey: "distMgAmount"
      },
      {
        name: "Distributor Corridor Of P&A And MG",
        explanation: "Combined Dist commission (if switch Yes).",
        formula: "R > (M+E)/(1−d) ? [(M+E)/(1−d)]×d : R×d · Else 0",
        valueKey: "distPaMgAmount"
      },
      {
        name: "Licensor Corridor Of P&A",
        explanation: "Lic corridor on P&A (if switch Yes).",
        formula:
          "Both Yes: R > E/(1−d−ℓ) ? [E/(1−d−ℓ)]×ℓ : R×ℓ · Dist No & Lic Yes: R > E/(1−d) ? [E/(1−d)]×ℓ : R×ℓ · Else 0",
        valueKey: "licPaAmount"
      },
      {
        name: "Licensor Corridor Of MG",
        explanation: "Lic corridor on MG (if switch Yes).",
        formula: "[M / (1 − ℓ_mg)] × ℓ_mg · Else 0",
        valueKey: "licMgAmount"
      },
      {
        name: "Total Less",
        explanation: "Everything deducted before overage.",
        formula:
          "P&A_in_less + MG_in_less + corridors",
        valueKey: "totalLess"
      }
    ]
  },
  {
    id: "overage",
    letter: "D",
    short: "Overage",
    title: "Overage",
    rows: [
      {
        name: "Cinematic Share Post Recoupment",
        explanation: "What’s left after Total Less.",
        formula: "Overage = R − Total Less",
        valueKey: "postRecoup"
      },
      {
        name: "Licensor’s Share Of Overage",
        explanation: "Licensor share of that overage.",
        formula:
          "If Overage > 0 → Overage × (xx / 100) · If Overage ≤ 0 → full Overage (no % cut)",
        valueKey: "overageShare"
      }
    ]
  },
  {
    id: "rights",
    letter: "E",
    short: "Rights",
    title: "Other rights table",
    intro: "Non-theatrical rights (TV, VOD, Demand, etc.). Rights with no revenue still appear at 0.",
    rows: [
      {
        name: "Rights subtotal (non-theatrical)",
        explanation: "Sum of all non-theatrical right shares.",
        formula: "Σ Licensor’s Share for all rights",
        valueKey: "rightsSubtotal"
      },
      {
        name: "Total Licensor Share",
        explanation: "Rights + theatrical overage share.",
        formula: "Σ right shares + Licensor’s Share Of Overage",
        valueKey: "totalLicensor"
      }
    ]
  },
  {
    id: "paid",
    letter: "F",
    short: "Already Paid",
    title: "Already Paid",
    rows: [
      {
        name: "MG (Already Paid)",
        explanation: "MG treated as already paid when Deduction MG = No.",
        formula: "If Deduction MG = No → M, else → 0",
        valueKey: "alreadyPaidMg"
      },
      {
        name: "Bumper Paid",
        explanation: "Bumper amounts already paid.",
        formula: "Σ contract bumper paid",
        valueKey: "bumperPaid"
      },
      {
        name: "Overage Paid",
        explanation: "Overages already paid.",
        formula: "Σ overage expenses",
        valueKey: "overagePaid"
      },
      {
        name: "Divers Paid",
        explanation: "Divers / other paid items.",
        formula: "Σ divers expenses",
        valueKey: "diversPaid"
      }
    ]
  },
  {
    id: "balance",
    letter: "G",
    short: "Balance",
    title: "Balance",
    rows: [
      {
        name: "Balance To Your Favour",
        explanation: "Result after already paid items.",
        formula:
          "Total Licensor Share − MG − Bumper − Overage Paid − Divers − other",
        valueKey: "balance"
      },
      {
        showConditionBanner: true,
        conditionText:
          "This corridor payable block is shown only when Licensor Corridor Prior To Recoupement Of P&A = Yes OR Licensor Corridor Prior To Recoupement Of MG = Yes.",
        name: "Total Licensor Corridor",
        explanation: "Sum of licensor corridor amounts before overage.",
        formula: "Lic P&A corridor + Lic MG corridor",
        valueKey: "totalLicCorridor",
        corridorOnly: true
      },
      {
        name: "Less Licensor Corridor Paid",
        explanation: "Corridor already paid.",
        formula: "Σ corridor paid",
        valueKey: "corridorPaid",
        corridorOnly: true
      },
      {
        name: "Final / Total Payable",
        explanation: "Combined payable when corridor section is active.",
        formula:
          "Main ≤ 0 & corridor ≥ 0 → corridor only · Both ≥ 0 → main Balance + corridor",
        valueKey: "finalPayable",
        corridorOnly: true
      }
    ]
  }
];

function ynFromFlag(v) {
  if (v === true || v === 1 || v === "1" || v === "Yes" || v === "yes") return "Yes";
  if (v === false || v === 0 || v === "0" || v === "No" || v === "no") return "No";
  if (v == null || v === "") return "—";
  return String(v);
}

function firstFlag(arrOrVal) {
  if (Array.isArray(arrOrVal)) return arrOrVal[0];
  return arrOrVal;
}

function money(n) {
  if (n == null || n === "" || Number.isNaN(Number(n))) return null;
  return formatCurrency(Number(n));
}

export default {
  name: "RoyaltyReportInfoModal",
  components: { BModal, BButton, BAlert },
  props: {
    value: { type: Boolean, default: false },
    isTerritory: { type: Boolean, default: false },
    reportData: { type: Object, default: null }
  },
  data() {
    return {
      sections: SECTIONS,
      activeSection: "switches"
    };
  },
  computed: {
    hasReport() {
      return !!this.reportData;
    },
    alwaysShowCorridorInfo() {
      // Keep corridor rows visible in Report Info so users can learn the rule,
      // even when the section is hidden on the statement.
      return true;
    },
    corridorSectionVisible() {
      const pa = this.live("licCorridorPa");
      const mg = this.live("licCorridorMg");
      return pa === "Yes" || mg === "Yes";
    },
    liveMap() {
      const d = this.reportData;
      if (!d) return {};

      if (this.isTerritory) {
        const tc = d.totalColumn || {};
        const pnaDisplay = (d.pnaSection || []).reduce(
          (s, x) => s + (parseFloat(x.totalValue) || 0),
          0
        );
        const rentalAcc = (d.rentalSection?.[0]?.rental || []).reduce(
          (s, x) => s + (parseFloat(x.totalValue) || 0),
          0
        );
        const rentalPeriod = (d.rentalSection?.[1]?.rental || []).reduce(
          (s, x) => s + (parseFloat(x.totalValue) || 0),
          0
        );
        const admissions = (d.rentalSection?.[2]?.admission || []).reduce(
          (s, x) => s + (parseFloat(x.totalValue) || 0),
          0
        );
        const boxOffice = (d.rentalSection?.[2]?.boxOffice || []).reduce(
          (s, x) => s + (parseFloat(x.totalValue) || 0),
          0
        );
        const overagePct = d.licenorShareOfOveragePercent;
        return {
          deductionPa: ynFromFlag(d.deductionPaFromCinemeticGrossReceipts),
          deductionMg: ynFromFlag(d.deductionMgFromCinematicGrossReceipts),
          distCorridorPa: ynFromFlag(d.distributorCorridorPriorToRecoupementOfPa),
          distCorridorMg: ynFromFlag(d.distributorCorridorPriorToRecoupementOfMg),
          distCorridorPaMg: ynFromFlag(
            d.distributorCorridorPriorToRecoupementOfPaAndMg
          ),
          licCorridorPa: ynFromFlag(d.licensorCorridorPriorToRecoupementOfPa),
          licCorridorMg: ynFromFlag(d.licensorCorridorPriorToRecoupementOfMg),
          rentalAcc: money(rentalAcc),
          rentalPeriod: money(rentalPeriod),
          totalRental: money(tc.rentalTotalSum),
          admissionsTotal: String(admissions || 0),
          boxOfficeTotal: money(boxOffice),
          pnaTotal: money(pnaDisplay),
          pnaInLess: money(tc.pnaTotalSum),
          mgInLess: money(tc.mgPaTotal),
          distPaAmount: money(tc.distributorCorridorPriorToRecoupementOfPaSectionTotal),
          distMgAmount: money(tc.distributorCorridorPriorToRecoupementOfMgTotal),
          distPaMgAmount: money(tc.distributorCorridorPriorToRecoupementOfPaAndMgTotal),
          licPaAmount: money(tc.licensorCorridorPriorToRecoupementOfPaSectionTotal),
          licMgAmount: money(tc.licensorCorridorPriorToRecoupementOfMgTotal),
          totalLess: money(tc.lessSectionTotal),
          postRecoup: money(tc.cinemeticSharePostRecoupmentSectionTotal),
          overageShare: money(tc.licenorShareOfOverageSectionTotal),
          overagePercent: overagePct != null && overagePct !== "" ? `${overagePct}%` : "",
          rightsSubtotal: money(tc.territorySumTotal),
          totalLicensor: money(tc.totalLicensorSum),
          alreadyPaidMg: money(tc.lessPaidMgTotal),
          bumperPaid: money(tc.lessBumperPaidTotal),
          overagePaid: money(tc.lessOveragePaidTotal),
          diversPaid: money(tc.lessDiversPaidTotal),
          balance: money(tc.actualBalancePayable ?? tc.balancePayable),
          totalLicCorridor: money(tc.totalLicensorCoridor),
          corridorPaid: money(tc.lessLicensorCorridorPaid),
          finalPayable: money(tc.totalFinalPayable || tc.actualBalancePayable),
          territories: tc.contractTerritories || "—"
        };
      }

      // Default / standard report
      const paFlag = firstFlag(d.Deduction_from_Rental_PA);
      const mgFlag = firstFlag(d.Deduction_from_Rental_MG);
      const rightsRows = Array.isArray(d.licensorArray?.[0])
        ? d.licensorArray[0]
        : Array.isArray(d.licensorArray)
        ? d.licensorArray
        : [];
      const rightsSum = rightsRows.reduce(
        (s, r) => s + (parseFloat(r.totalFinal) || 0),
        0
      );
      const totalLessApprox =
        (paFlag == 0 || paFlag === "0" ? 0 : parseFloat(d.total_expense_amount) || 0) +
        (parseFloat(d.MG_PA) || 0) +
        (parseFloat(d.distributor_Share_prior_to_recoupement_new) || 0) +
        (parseFloat(d.Distributor_Share_prior_to_recoupement_on_MG_val) || 0) +
        (parseFloat(d.Distributor_Commission_on_Rental_val) || 0) +
        (parseFloat(d.Licensor_Share_prior_to_recoupement_on_PA_val) || 0) +
        (parseFloat(d.Licensor_Share_prior_to_recoupement_on_MG_val) || 0);

      return {
        deductionPa: ynFromFlag(paFlag),
        deductionMg: ynFromFlag(mgFlag),
        distCorridorPa: ynFromFlag(firstFlag(d.Distributor_Share_prior_to_recoupement_on_PA)),
        distCorridorMg: ynFromFlag(firstFlag(d.Distributor_Share_prior_to_recoupement_on_MG)),
        distCorridorPaMg: ynFromFlag(firstFlag(d.Distributor_Commission_on_Rental)),
        licCorridorPa: ynFromFlag(firstFlag(d.Licensor_Share_prior_to_recoupement_on_PA)),
        licCorridorMg: ynFromFlag(firstFlag(d.Licensor_Share_prior_to_recoupement_on_MG)),
        rentalAcc: money(d.accuRentalSum),
        rentalPeriod: money(d.periodRentalSum),
        totalRental: money(d.totalRentalSum),
        admissionsTotal: String(d.totalAddmissionSum || 0),
        boxOfficeTotal: money(d.totalBoxOfficeSum),
        pnaTotal: money(d.total_expense_amount),
        pnaInLess: money(
          paFlag == 0 || paFlag === "0" ? 0 : d.total_expense_amount
        ),
        mgInLess: money(d.MG_PA),
        distPaAmount: money(d.distributor_Share_prior_to_recoupement_new),
        distMgAmount: money(d.Distributor_Share_prior_to_recoupement_on_MG_val),
        distPaMgAmount: money(d.Distributor_Commission_on_Rental_val),
        licPaAmount: money(d.Licensor_Share_prior_to_recoupement_on_PA_val),
        licMgAmount: money(d.Licensor_Share_prior_to_recoupement_on_MG_val),
        totalLess: money(d.allocation_torwards_pa_final ?? totalLessApprox),
        postRecoup: money(d.theatrical_overages),
        overageShare: money(d.Distributor_Theatrical_Share),
        overagePercent:
          d.Distributor_Theatrical_Share_percent != null &&
          d.Distributor_Theatrical_Share_percent !== ""
            ? `${d.Distributor_Theatrical_Share_percent}%`
            : "",
        rightsSubtotal: money(rightsSum),
        totalLicensor: money(d.liencer_sum1),
        alreadyPaidMg: money(d.Already_paid_MG),
        bumperPaid: money(d.BUMPER_PAID),
        overagePaid: money(d.OVERAGES_PAID),
        diversPaid: money(d.DIVERS_PAID),
        balance: money(d.Balance_to_your_favor),
        totalLicCorridor: money(
          (parseFloat(d.Licensor_Share_prior_to_recoupement_on_PA_val) || 0) +
            (parseFloat(d.Licensor_Share_prior_to_recoupement_on_MG_val) || 0)
        ),
        corridorPaid: money(d.Less_Licensor_corridor_Paid),
        finalPayable: money(d.total_payable || d.Balance_payable)
      };
    }
  },
  methods: {
    live(key) {
      if (!key) return null;
      return this.liveMap[key] ?? null;
    },
    onVisibleChange(val) {
      this.$emit("input", val);
      if (val) this.activeSection = "switches";
    },
    scrollToSection(id) {
      this.activeSection = id;
      const el = document.getElementById("info-sec-" + id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }
};
</script>

<style scoped>
.report-info-header {
  background: #1e3a5f;
  margin: -1rem -1rem 0;
  padding: 1rem 1.25rem;
  border-radius: 0.3rem 0.3rem 0 0;
}
.close-btn {
  font-size: 1.25rem;
  line-height: 1;
  opacity: 0.85;
  text-decoration: none !important;
}
.close-btn:hover {
  opacity: 1;
  color: #fff !important;
}
.report-info-body {
  background: #f4f6f9;
}
.legend-strip {
  padding: 1rem 1.25rem 0.75rem;
  background: #fff;
  border-bottom: 1px solid #e6ebf1;
}
.legend-title {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6e7a8a;
  margin-bottom: 0.5rem;
}
.legend-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: #eef3f9;
  color: #2c3e50;
  border: 1px solid #d5e0ec;
  border-radius: 999px;
  padding: 0.3rem 0.7rem;
  font-size: 0.78rem;
}
.chip strong {
  color: #1e3a5f;
  font-family: Consolas, Monaco, monospace;
}
.live-banner {
  display: inline-block;
  background: #e8f5ee;
  color: #1b6b45;
  border: 1px solid #b7e0c8;
  border-radius: 6px;
  padding: 0.3rem 0.65rem;
  font-size: 0.75rem;
  font-weight: 600;
}
.section-nav {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  padding: 0.75rem 1.25rem;
  background: #fff;
  border-bottom: 1px solid #e6ebf1;
  position: sticky;
  top: 0;
  z-index: 2;
}
.nav-pill {
  border: 1px solid #d0d7e2;
  background: #fff;
  color: #4a5568;
  border-radius: 999px;
  padding: 0.28rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
}
.nav-pill:hover {
  border-color: #1e3a5f;
  color: #1e3a5f;
}
.nav-pill.active {
  background: #1e3a5f;
  border-color: #1e3a5f;
  color: #fff;
}
.sections-scroll {
  padding: 1rem 1.25rem 1.5rem;
  max-height: min(70vh, 640px);
  overflow-y: auto;
}
.info-section {
  background: #fff;
  border: 1px solid #e6ebf1;
  border-radius: 10px;
  padding: 1rem 1.1rem;
  margin-bottom: 0.85rem;
}
.section-head {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  margin-bottom: 0.85rem;
}
.section-index {
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  border-radius: 8px;
  background: #1e3a5f;
  color: #fff;
  font-weight: 700;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
}
.sub-label {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #8895a7;
  margin-bottom: 0.4rem;
}
.item-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 0.5rem;
}
.item-list {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}
.info-card {
  background: #f8fafc;
  border: 1px solid #e8eef5;
  border-radius: 8px;
  padding: 0.7rem 0.85rem;
}
.info-card.compact {
  background: #fff;
}
.row-main {
  display: flex;
  gap: 1rem;
  align-items: stretch;
  justify-content: space-between;
}
.row-left {
  flex: 1;
  min-width: 0;
}
.row-right {
  flex-shrink: 0;
  width: 150px;
  background: #fff;
  border: 1px solid #d5e0ec;
  border-radius: 8px;
  padding: 0.55rem 0.65rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: right;
}
.value-label {
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #8895a7;
  margin-bottom: 0.2rem;
}
.value-display {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1e3a5f;
  word-break: break-word;
  font-variant-numeric: tabular-nums;
}
.value-display.yn {
  font-size: 1.05rem;
}
.value-display.empty {
  color: #a0aab8;
  font-weight: 600;
}
.value-pill {
  margin-top: 0.4rem;
  display: inline-block;
  background: #eef3f9;
  border: 1px solid #d5e0ec;
  border-radius: 6px;
  padding: 0.2rem 0.45rem;
  font-size: 0.78rem;
  font-weight: 700;
  color: #1e3a5f;
}
.condition-banner {
  background: #fff8e8;
  border: 1px solid #f0d9a0;
  border-radius: 8px;
  padding: 0.75rem 0.9rem;
  margin-bottom: 0.15rem;
}
.condition-title {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #8a6d2b;
  margin-bottom: 0.25rem;
}
.condition-text {
  font-size: 0.82rem;
  color: #4a3b1a;
  line-height: 1.45;
  margin-bottom: 0.45rem;
}
.condition-live {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem 0.6rem;
  font-size: 0.78rem;
  color: #5a6577;
}
.condition-live .on {
  color: #1b6b45;
}
.condition-live .off {
  color: #9a5b1a;
}
.condition-switches {
  width: 100%;
  color: #6e7a8a;
}
.condition-switches b {
  color: #1e3a5f;
}
.info-card.dimmed {
  opacity: 0.72;
}
.item-title {
  font-weight: 700;
  font-size: 0.86rem;
  color: #1a2332;
  margin-bottom: 0.2rem;
}
.item-desc {
  font-size: 0.8rem;
  color: #5a6577;
  line-height: 1.4;
}
.formula-box,
.rule-box {
  margin-top: 0.5rem;
  background: #1e3a5f;
  color: #e8f0fa;
  border-radius: 6px;
  padding: 0.45rem 0.65rem;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}
.rule-box {
  background: #f0f4f8;
  color: #2c3e50;
  border: 1px solid #d5e0ec;
}
.formula-label {
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  opacity: 0.75;
}
.formula-box code {
  color: #fff;
  font-size: 0.78rem;
  white-space: pre-wrap;
  word-break: break-word;
  background: transparent;
  padding: 0;
}
.rule-box span:last-child {
  font-size: 0.8rem;
}
.example-section {
  border-color: #c5d4e8;
  background: #f7fafc;
}
.example-flow {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 0.35rem;
}
.ex-step {
  background: #fff;
  border: 1px solid #d5e0ec;
  border-radius: 8px;
  padding: 0.55rem 0.7rem;
  min-width: 110px;
  flex: 1;
}
.ex-step.muted {
  opacity: 0.85;
}
.ex-step.highlight {
  border-color: #1e3a5f;
  background: #1e3a5f;
  color: #fff;
}
.ex-step.highlight .ex-hint {
  color: rgba(255, 255, 255, 0.75);
}
.ex-label {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  opacity: 0.7;
}
.ex-value {
  font-size: 0.95rem;
  font-weight: 700;
  margin-top: 0.15rem;
}
.ex-hint {
  font-size: 0.68rem;
  color: #6e7a8a;
  margin-top: 0.1rem;
}
.ex-arrow {
  display: flex;
  align-items: center;
  color: #8895a7;
  font-weight: 700;
  padding: 0 0.1rem;
}
@media (max-width: 768px) {
  .row-main {
    flex-direction: column;
  }
  .row-right {
    width: 100%;
    text-align: left;
  }
  .example-flow .ex-arrow {
    display: none;
  }
  .ex-step {
    min-width: calc(50% - 0.35rem);
  }
}
</style>
<style>
.royalty-report-info-modal .modal-header {
  padding: 0;
  border-bottom: none;
}
.royalty-report-info-modal .modal-content {
  overflow: hidden;
}
</style>
